import type { PersistZebraEventParams } from '../api/order.api'
import type { OrderDetails } from './order.types'
import type { ProofOfAgePayload } from '@/features/order/model'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { stateKeys } from '@/entities/state/model'
import { enqueueZebraEvent, recordRejectedZebraEvent } from '@/features/order/lib/zebraEventQueue'
import { usePositiveFeedbackStore } from '@/features/positive-feedback/model/positiveFeedback.store'
import { isPermanentRejection } from '@/shared/api'
import { persistZebraEvent, updateAcknowledgeOrder, updateOrderStatus, updateOrderTmInitials } from '../api/order.api'
import { orderKeys } from './order.queryKeys'

export interface UpdateOrderStatusParams {
  orderId: string
  newStatus: string
  lastUpdateTime?: string
  verificationData?: {
    verificationCode?: string
    verificationErrorCode?: string
    exceptionReason?: string
  }
  /** Proof of Age audit record for a restricted order (BD-8253). */
  proofOfAge?: ProofOfAgePayload
}

/**
 * Hook for updating order status with optimistic updates
 *
 * This hook can be used in any component that needs to update order status
 * and provides optimistic updates for a better user experience.
 */
export function useOrderStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: UpdateOrderStatusParams) => updateOrderStatus<OrderDetails>(params),

    // Optimistic update
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: orderKeys.orders() })

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())

      // Optimistically update to the new value
      if (previousOrders) {
        queryClient.setQueryData<OrderDetails[]>(
          orderKeys.orders(),
          previousOrders.map(order =>
            order.orderId === variables.orderId
              ? { ...order, status: variables.newStatus }
              : order,
          ),
        )
      }

      return { previousOrders }
    },

    // Update with the response data
    onSuccess: (data: OrderDetails, variables) => {
      // Get the current orders data
      const currentOrders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())
      if (currentOrders) {
        // Update the order with the response data
        queryClient.setQueryData<OrderDetails[]>(
          orderKeys.orders(),
          currentOrders.map(order =>
            order.orderId === variables.orderId
              ? {
                  ...order,
                  ...data, // Update with all the response data
                }
              : order,
          ),
        )
      }

      if (data?.positiveFeedback?.shouldTrigger === true) {
        usePositiveFeedbackStore
          .getState()
          .trigger('Great job! Order completed within the wait time 🎉')
      }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    // and check if we need to invalidate state config
    onError: (error: any, _variables, context: any) => {
      // Roll back optimistic update
      if (context?.previousOrders) {
        queryClient.setQueryData(orderKeys.orders(), context.previousOrders)
      }

      const errorType: string | undefined = error?.data?.errorType
      const errorDescription: string | undefined = error?.data?.errorMessages?.errorDescription

      // Show the backend's validation message so the user knows why the transition failed
      if (errorType === 'ValidationError' && errorDescription) {
        notifications.show({
          color: 'red',
          title: 'Unable to update order',
          message: errorDescription,
          autoClose: 6000,
        })
      }

      // Check if error indicates invalid state transition or outdated state config
      const isStateConfigError = error?.status === 400
        && errorType === 'ValidationError'
        && errorDescription?.includes('support state')

      // If this is a state config error and we haven't already invalidated
      if (isStateConfigError && !context?.stateConfigInvalidated) {
        // Invalidate state config to force refresh
        queryClient.invalidateQueries({ queryKey: stateKeys.stateConfig() })

        // Mark that we've already invalidated to prevent multiple invalidations
        // during retries of the same failed mutation
        if (context) {
          context.stateConfigInvalidated = true
        }

        // Log the error for debugging
        console.warn('Detected possible state template change, refreshing state config')
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.orders() })
      queryClient.invalidateQueries({ queryKey: orderKeys.waitTime() })
    },
  })
}

/**
 * Hook for persisting Zebra scan and missing-crate events immediately.
 * When offline, enqueues the event for later flush on reconnect instead of burning retries.
 * Failures after retry exhaustion are also enqueued as a fallback.
 */
/**
 * What to do when a Zebra event cannot be persisted.
 *
 * BD-8548: a refusal must never read as a recorded crate. Retrying a 4xx changes
 * nothing, so queueing it would only move the failure out of sight — the team member is
 * told instead, and the event is kept for support to inspect.
 *
 * Anything else is a failed attempt rather than a refused payload, so the event waits in
 * the queue and is replayed on reconnect (BD-8547).
 *
 * Exported so it can be tested without driving the mutation's retry backoff.
 */
export function handleZebraEventError(error: unknown, variables: PersistZebraEventParams): void {
  console.error(`Failed to persist zebra event for order ${variables.orderId}, eventType ${variables.eventType}:`, error)

  if (isPermanentRejection(error)) {
    recordRejectedZebraEvent(variables, error instanceof Error ? error.message : String(error))
    notifications.show({
      title: 'Not recorded',
      message: variables.eventType === 'missingCrates'
        ? 'That crate was not saved as missing. Please tell your manager.'
        : 'That scan was not saved. Please tell your manager.',
      autoClose: 6000,
    })
    return
  }

  enqueueZebraEvent(variables)
}

export function usePersistZebraEventMutation() {
  return useMutation({
    mutationFn: (params: PersistZebraEventParams) => {
      if (!navigator.onLine) {
        enqueueZebraEvent(params)
        return Promise.resolve(undefined)
      }
      return persistZebraEvent(params)
    },
    // BD-8600: TanStack pauses mutations while offline by default, which would hold the event
    // in memory and skip the offline branch above - lost if the app is killed before reconnect.
    // 'always' lets it reach the durable queue, which replays it on reconnect or next startup.
    networkMode: 'always',
    retry: 3,
    onError: handleZebraEventError,
  })
}

/**
 * Hook for writing team member initials against a completed order (and all of
 * its group members).
 *
 * Deliberately separate from `useOrderStatusMutation`: it does NOT touch the
 * positive-feedback store, so submitting initials never re-fires the confetti,
 * and it sends a `/tmInitials` patch (no status change) so the backend does not
 * recompute wait time.
 */
export function useOrderTmInitialsMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderIds, tmInitials }: { orderIds: string[], tmInitials: string }) => {
      await Promise.all(
        orderIds.map(orderId => updateOrderTmInitials<OrderDetails>({ orderId, tmInitials })),
      )
    },
    onSuccess: (_data, { orderIds, tmInitials }) => {
      // Reflect the captured initials on the cached orders (best-effort; the
      // backend is the source of truth for reporting).
      const currentOrders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())
      if (currentOrders) {
        const targetIds = new Set(orderIds)
        queryClient.setQueryData<OrderDetails[]>(
          orderKeys.orders(),
          currentOrders.map(order =>
            targetIds.has(order.orderId) ? { ...order, tm_initials: tmInitials } : order,
          ),
        )
      }
    },
    retry: 3,
  })
}

/**
 * Hook for acknowledging an order
 *
 * This mutation sets the `isAcknowledged` field to true for the specified order.
 */
export function useAcknowledgeOrderMutation() {
  return useMutation({
    mutationFn: async ({ orderId, lastUpdateTime }: { orderId: string, lastUpdateTime?: string }) => {
      return updateAcknowledgeOrder<OrderDetails>({ orderId, lastUpdateTime })
    },
    onError: (error, variables) => {
      console.error(`Failed to acknowledge order: ${variables.orderId}`, error)
    },
    retry: 3,
  })
}
